import React, { useEffect } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import { useConfigurationState } from "./hooks/useConfigurationState";
import { PageTitle } from "../PageTitle";
import { useUser } from "src/business-types/grocery/components/UserContext";

const PrintConfigTab = () => {
  const {
    newPrinterList,
    selectedPrinters,
    appPrinterEnabled,
    setAppPrinterEnabled,
    handlePrinterCheckboxChange,
    savePrinterSettings,
    disableAppPrinter,
  } = useConfigurationState();
  const { user } = useUser();

  const [initialState, setInitialState] = React.useState({
    appPrinterEnabled,
    selectedPrinters,
  });

  useEffect(() => {
    setInitialState({
      appPrinterEnabled,
      selectedPrinters,
    });
  }, [appPrinterEnabled, selectedPrinters]);

  const isPrinterListChanged =
    selectedPrinters.length !== initialState.selectedPrinters.length || !selectedPrinters.every((p) => initialState.selectedPrinters.includes(p));

  const isChanged = appPrinterEnabled !== initialState.appPrinterEnabled || isPrinterListChanged;

  const handleSave = () => {
    savePrinterSettings();

    setInitialState({
      appPrinterEnabled,
      selectedPrinters,
    });
  };

  useEffect(() => {
    if (user?.businessType !== "Grocery") {
      document.title = "Print Config› Configuration › iMetanic";
    }
  }, []);

  return (
    <div className='w-full h-fit flex justify-center items-center p-4'>
      <PageTitle title='- Configuration ›  Print Config' />
      <div className='w-full lg:w-1/2 border border-blue-100 shadow-xl rounded-2xl p-6 bg-white'>
        <h2 className='text-2xl font-bold mb-6 text-center'>Printer Configuration</h2>

        {/* Enable Printer Toggle */}
        <div className='mb-6'>
          <label className='flex items-center gap-3 cursor-pointer'>
            <input type='checkbox' checked={appPrinterEnabled} onChange={() => setAppPrinterEnabled(!appPrinterEnabled)} className='w-4 h-4' />
            <span className='text-lg font-semibold'>Enable App Printer</span>
          </label>
        </div>

        {/* Printer List Section */}
        <div className='relative'>
          {!appPrinterEnabled && <div className='absolute inset-0 bg-white/60 backdrop-blur-sm z-20 rounded-lg' />}

          <div className='w-full p-4 border border-gray-200 rounded-lg shadow-sm space-y-3'>
            <h3 className='text-lg font-semibold mb-3'>Available Printers</h3>

            {newPrinterList?.length > 0 ? (
              newPrinterList.map((item: string, index: number) => (
                <label key={index} className='flex items-center gap-3 cursor-pointer p-2 hover:bg-gray-50 rounded'>
                  <input
                    type='checkbox'
                    checked={selectedPrinters.includes(item)}
                    onChange={() => handlePrinterCheckboxChange(item)}
                    className='w-4 h-4'
                  />
                  <span className='text-sm'>{item}</span>
                </label>
              ))
            ) : (
              <p className='text-sm text-gray-500 text-center py-4'>No printers found</p>
            )}
          </div>

          {appPrinterEnabled && (
            <div className='flex justify-center gap-4 mt-4'>
              <Button label='Save Printer Settings' onClick={savePrinterSettings} disabled={selectedPrinters.length === 0} />
            </div>
          )}
        </div>

        {/* Disable Printer Button (when enabled) */}
        {appPrinterEnabled && (
          <div className='flex justify-center gap-4 mt-6'>
            <Button label='Disable App Printer' variant='danger' onClick={disableAppPrinter} />
          </div>
        )}

        {/* Enable Printer Button (when disabled) */}
        {!appPrinterEnabled && (
          <div className='flex justify-center gap-4 mt-6'>
            <Button
              label='Enable App Printer'
              onClick={() => {
                setAppPrinterEnabled(true);
                savePrinterSettings();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintConfigTab;
